import { API_BASE_URL } from '@/environment'
import { downloadFile } from '@/helpers/downloadFile'
import { apiV3Admin } from '@/service/v3/apiV3'
import type { ApiCourse } from '@attensi/v3-client-wip/dist/esm/__admin__prod__/generated'
import type { RegistrationStatus } from '@/gql/graphql'
import { customFetch } from '../apiV3Utils'
import { customFetchMedia } from '../apiV3UtilsMedia'
import { getCourseMock, getEventMock } from './mocks/eventAPIMock'
import type { EventWithIncluded } from './types/eventTypes'
import type { EventFormValues } from '@/components/product/product_components/component_events/date_form_components/EventDateForm.vue'

export type { EventDate } from './types/eventTypes'

type Promisable<T> = T | Promise<T>

const fetchWrapper = <
  ApiType extends (...args: any) => any,
  MockType extends (...args: any) => any,
>(
  api: ApiType,
  mock: MockType,
  ...args: any
): ReturnType<ApiType> | ReturnType<MockType> => {
  const useMocks = import.meta.env.VITE_APP_USE_MOCKS
  return useMocks ? mock(...args) : api(...args)
}

export const getEvent = (eventId: number): Promisable<EventWithIncluded> => {
  const eventWithCourses = (eventId: number) => {
    return apiV3Admin.products.adminListProducts(eventId, {
      include: 'courses',
    }) as Promise<EventWithIncluded>
  }
  return fetchWrapper(eventWithCourses, getEventMock, eventId, {
    include: 'courses',
  })
}

export const getDate = (dateId: number): Promisable<ApiCourse> => {
  // Need to define interfaces for several endpoint returns
  return fetchWrapper(apiV3Admin.courses.adminGetCourse, getCourseMock, dateId)
}

export const addUserToAttendingList = (dateID: number, userId: number) => {
  const url = `${API_BASE_URL}/api/v3/admin/courses/${dateID}/attendees`

  const form = new FormData()
  form.set('data[attributes][user_id]', userId.toString())

  return customFetchMedia(url, { method: 'POST', body: form })
}

export const removeUserFromAttendingList = (dateId: number, userId: number) => {
  const url = `${API_BASE_URL}/api/v3/admin/courses/${dateId}/attendees/${userId}`
  return customFetch(url, { method: 'DELETE' })
}

export const downloadAttendees = async (
  courseId: number,
  fileExtension: 'xlsx' | 'csv',
  statuses?: RegistrationStatus[]
) => {
  const params = new URLSearchParams({ file_extension: fileExtension })
  statuses?.forEach((status) => {
    params.append('status[]', status)
  })
  const url = `${API_BASE_URL}/api/v3/admin/courses/${courseId}/attendees/export?${params}`

  const attendees = await customFetch(url, { method: 'GET' })

  const attendeesBlob = await attendees.blob()
  const fileName = `${courseId}_exported_attendees_${new Date().toISOString()}.${fileExtension}`

  downloadFile(attendeesBlob, fileName)
}

export type EventFormDTO = {
  location: string
  room: string
  instructor: string
  instructor_id: string | number
  start_datetime: string
  end_datetime: string
  signup_deadline: string
  attendees_count_max: number
  course_material: string // create type LinkString
  meeting_link: string
}

export const createEventDate = async (
  productId: number,
  eventDateForm: EventFormValues
) => {
  const url = `${API_BASE_URL}/api/v3/admin/products/${productId}/courses`
  const postResponse = await customFetch(url, {
    method: 'POST',
    body: JSON.stringify({
      data: {
        type: 'courses',
        attributes: eventDateForm,
      },
    }),
  })

  return postResponse.json()
}

export const updateProductEvent = async (
  productId: number,
  dateId: number,
  eventDateForm: EventFormValues
) => {
  const url = `${API_BASE_URL}/api/v3/admin/products/${productId}/courses/${dateId}`
  const patchResponse = await customFetch(url, {
    method: 'PATCH',
    body: JSON.stringify({
      data: {
        id: dateId,
        type: 'courses',
        attributes: eventDateForm,
      },
    }),
  })

  return patchResponse.json()
}

export const deleteEventDate = async (dateId: number) => {
  const url = `${API_BASE_URL}/api/v3/admin/courses/${dateId}`
  const deleteResponse = await customFetchMedia(url, {
    method: 'DELETE',
  })
  return deleteResponse.json
}

export const downloadCourseCalendar = async (
  courseId: number | string,
  locale?: string
) => {
  const localeParam = locale ? `?locale=${locale}` : ''
  const url = `${API_BASE_URL}/api/v3/admin/courses/${courseId}/calendar${localeParam}`

  const response = await customFetch(url, { method: 'GET' })

  if (!response.ok)
    throw new Error(`Failed to download calendar: ${response.status}`)

  const blob = await response.blob()
  downloadFile(blob, `course-${courseId}.ics`)
}
